import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { Skill } from '../types';

export const fixTextWrappingSkill: Skill = {
    name: SKILL_NAMES.fixTextWrapping,
    description:
        'Fixing widows, orphans, and awkward line wraps in text assets. Load when a tool finding reports a wrap problem.',
    kind: 'howto',
    surfaces: ['sketch'],
    get instructions(): string {
        return `# Fixing Text Wrapping

A widow is a rendered line holding a single stranded word:

> Then the coffee is ground into a
> powder

## Before you touch anything
- **The user's words are fixed.** If they supplied the copy — a script, approved or
  legal-reviewed wording, a quote — fix the wrap with geometry alone. Reword only if
  nothing else works, and say so when you do.
- **Look at the scene.** The serialized state cannot tell you what the text sits on, and
  the right dial depends on it — render the scene with \`${TOOL_NAMES.imageAsk}\` and
  classify the backdrop before touching anything.
- **Find the siblings.** If the flagged asset is one row of a matched set (list rows,
  steps, stacked labels — other text in the scene with the same font and size), the set
  must stay uniform: either reword the flagged row alone, or apply the identical
  geometry change to every sibling. Never restyle just the one row.

## Pick ONE dial — by what the text sits on
- **A panel that hugs the text** (drawn by the text's own group, it resizes with the
  text — a floating callout over media): adjust the **box width** and nothing else.
  Prefer *narrowing* toward two balanced lines; never leave the block wider than the
  design needs. Leave the font size alone unless it's a crazy amount of text.
  The panel follows the box, so width by itself rebalances the wrap for normal amounts of text.
- **A fixed backdrop** (a shape, banner, or color block that does NOT resize with the
  text): **never touch the width** — wider spills off the backdrop, narrower looks
  arbitrary. Shrink the **font size** a step or two instead, or reword when the words
  are yours to change.
- **Bare text over the background**: width or font size both work. A resized block that
  was centered must be re-centered — side margins balanced again — via the \`center\`
  position preset or an explicit position.

**One dial at a time.** If a width didn't clear the widow, try another width before
switching dials — and if you switch, put the abandoned dial back to its original value.
A fix that lands as "narrower box AND smaller font" when either alone would do has
changed the design twice for one problem.

## Also
- **Too much text** (three-plus lines wherever it sits): geometry won't save it — split
  it into more scenes/subscenes at a natural break. Aim for one or two balanced lines.
- **Moving text**: reach for \`${TOOL_NAMES.setTextPositionPreset}\` first (check the scene's
  \`textPositionPresets\`); manual coordinates only when no preset fits.
- **Whole block off or cramped**: control the group with \`${TOOL_NAMES.setGroupAsset}\`
  (\`position\`, \`max_height\`, \`align_children\`) rather than stretching one child.
- **Shape-bounded assets** (circle, badge, pill): the shape can't grow. Either put a
  one-or-two-word label in it or switch to a design whose text area fits the phrase.

## Confirm
After each change, read ${TOOL_NAMES.setText}'s rendered-lines output and repeat until every
line holds two or more words. Before you finish, verify the geometry you leave behind:
- a resized block that was centered is re-centered (side margins balanced in the bounds);
- matched rows still share one font size and one width;
- any dial you tried and abandoned is back at its original value;
- if you changed the width, render the scene again — the backdrop must still fit.
Don't sign off on a box with a widow — or a layout you bent to get rid of one.`;
    },
};
